import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { ApplicationName, ApplicationPaths } from "./ApiAuthorizationConstants";

export type AuthenticationResult =
  | { status: typeof AuthenticationResultStatus.Redirect }
  | { status: typeof AuthenticationResultStatus.Success; state: unknown }
  | { status: typeof AuthenticationResultStatus.Fail; message: string };

export const AuthenticationResultStatus = {
  Redirect: "redirect" as const,
  Success: "success" as const,
  Fail: "fail" as const,
};

type Callback = () => void;
type Subscription = { callback: Callback; subscription: number };

class AuthorizeService {
  private _callbacks: Subscription[] = [];
  private _nextSubscriptionId = 0;
  private _user: User | null = null;
  private _isAuthenticated = false;
  private _signinRedirectPending = false;
  userManager?: UserManager;

  async isAuthenticated(): Promise<boolean> {
    await this.ensureUserManagerInitialized();
    const user = await this.userManager!.getUser();
    return !!user && !user.expired;
  }

  async getUser(): Promise<Record<string, unknown> | null> {
    await this.ensureUserManagerInitialized();
    const user = await this.userManager!.getUser();
    // Always read from storage — never return a stale cached profile.
    // The cached _user may have an expired token that was already removed
    // from storage, which would make redirectToLogin() think the user is
    // still logged in when they are not.
    if (!user || user.expired) return null;
    return (user.profile as Record<string, unknown>) ?? null;
  }

  async getAccessToken(): Promise<string | null> {
    await this.ensureUserManagerInitialized();
    const user = await this.userManager!.getUser();
    if (!user?.access_token) return null;
    // If the token is expired (or within 5 seconds of expiry) treat it as absent
    // so callers don't send a dead Bearer token to the server.
    if (user.expired) return null;
    return user.access_token;
  }

  async signIn(state?: unknown): Promise<AuthenticationResult> {
    await this.ensureUserManagerInitialized();
    if (this._signinRedirectPending) {
      return this.redirect();
    }
    try {
      this._signinRedirectPending = true;
      await this.userManager!.signinRedirect(this.createArguments(state));
      return this.redirect();
    } catch (redirectError) {
      this._signinRedirectPending = false;
      console.error("Redirect authentication error: ", redirectError);
      return this.error(redirectError);
    }
  }

  async completeSignIn(url: string): Promise<AuthenticationResult> {
    try {
      await this.ensureUserManagerInitialized();
      const user = await this.userManager!.signinCallback(url);
      this.updateState(user ?? null);
      return this.success(user?.state);
    } catch (error) {
      console.error("There was an error signing in: ", error);
      return this.error(`There was an error signing in: ${error instanceof Error ? error.message : error}`);
    }
  }

  async signOut(_state?: unknown): Promise<AuthenticationResult> {
    await this.ensureUserManagerInitialized();
    try {
      // Navigate FIRST — before removing the user / notifying subscribers.
      // Calling updateState(undefined) first fires notifySubscribers() synchronously
      // and every AuthorizeRoute races to redirect to /login before the page
      // navigates to /connect/endsession.
      const postLogoutUri = `${window.location.origin}${ApplicationPaths.LogOutCallback}`;
      window.location.replace(`/connect/endsession?post_logout_redirect_uri=${encodeURIComponent(postLogoutUri)}`);
      await this.userManager!.removeUser();
      this.updateState(null);
      return this.redirect();
    } catch (error) {
      console.error("Sign out error: ", error);
      return this.error(error);
    }
  }

  async completeSignOut(_url: string): Promise<AuthenticationResult> {
    await this.ensureUserManagerInitialized();
    try {
      // We bypassed oidc-client-ts for the signout redirect so there is no
      // oidc state to process — just clear local state and return success.
      await this.userManager!.removeUser();
      this.updateState(null);
      return this.success(null);
    } catch (error) {
      console.error(`There was an error trying to log out: ${error}`);
      return this.error(error);
    }
  }

  updateState(user: User | null): void {
    const wasAuthenticated = this._isAuthenticated;
    this._user = user;
    this._isAuthenticated = !!this._user;
    // Only notify subscribers when the authentication state actually changes
    // (signed-in ↔ signed-out). Silent token renewals update the user object
    // but should NOT trigger re-renders that would unmount/remount components
    // and destroy form state.
    if (wasAuthenticated !== this._isAuthenticated) {
      this.notifySubscribers();
    }
  }

  subscribe(callback: Callback): number {
    this._callbacks.push({ callback, subscription: this._nextSubscriptionId++ });
    return this._nextSubscriptionId - 1;
  }

  unsubscribe(subscriptionId: number): void {
    const index = this._callbacks.findIndex((s) => s.subscription === subscriptionId);
    if (index === -1) throw new Error(`Subscription ${subscriptionId} not found`);
    this._callbacks.splice(index, 1);
  }

  private notifySubscribers(): void {
    this._callbacks.forEach(({ callback }) => callback());
  }

  private createArguments(state?: unknown): Record<string, unknown> {
    return { useReplaceToNavigate: true, data: state };
  }

  private error(message: unknown): AuthenticationResult {
    const msg = message instanceof Error ? message.message : String(message);
    return { status: AuthenticationResultStatus.Fail, message: msg };
  }

  private success(state: unknown): AuthenticationResult {
    return { status: AuthenticationResultStatus.Success, state };
  }

  private redirect(): AuthenticationResult {
    return { status: AuthenticationResultStatus.Redirect };
  }

  private _initPromise?: Promise<void>;

  async ensureUserManagerInitialized(): Promise<void> {
    if (this.userManager !== undefined) return;
    if (!this._initPromise) {
      this._initPromise = this._initializeUserManager();
    }
    return this._initPromise;
  }

  private async _initializeUserManager(): Promise<void> {
    // Configure directly against the OIDC authority.
    // oidc-client-ts will auto-discover all endpoints from
    // /.well-known/openid-configuration — no /_configuration fetch needed.
    this.userManager = new UserManager({
      authority: `${window.location.origin}/`,
      client_id: ApplicationName,
      redirect_uri: `${window.location.origin}${ApplicationPaths.LoginCallback}`,
      post_logout_redirect_uri: `${window.location.origin}${ApplicationPaths.LogOutCallback}`,
      response_type: "code",
      scope: "openid profile email api offline_access",
      automaticSilentRenew: true,
      includeIdTokenInSilentRenew: false,
      loadUserInfo: false,
      filterProtocolClaims: false,
      userStore: new WebStorageStateStore({ prefix: ApplicationName }),
    });

    this.userManager.events.addUserSignedOut(async () => {
      await this.userManager!.removeUser();
      this.updateState(null);
    });

    // Silent renew succeeded — update the cached user so getUser() stays fresh.
    this.userManager.events.addUserLoaded((user) => {
      console.log("[AuthorizeService] User loaded (silent renew or sign-in)");
      this.updateState(user);
    });

    // Silent renew failed — log but don't nuke the session yet.
    // The token may still be valid for a few more seconds; let the
    // addAccessTokenExpired handler deal with the final expiry.
    this.userManager.events.addSilentRenewError((error) => {
      console.warn("[AuthorizeService] Silent renew failed:", error);
    });

    // Last resort: if the token actually expires (silent renew didn't save it),
    // clear local state so AuthorizeRoute redirects the user to login.
    this.userManager.events.addAccessTokenExpired(async () => {
      console.log("[AuthorizeService] Access token expired — clearing session");
      await this.userManager!.removeUser();
      this.updateState(null);
    });

    // Clean up expired OIDC state entries from interrupted login flows.
    this.userManager.clearStaleState();
  }

  static get instance(): AuthorizeService {
    return authService;
  }
}

const authService = new AuthorizeService();
export default authService;
